'use es6';

import shallowEqual from 'react-utils/shallowEqual';
import { arrangeNodePositionSubsetById, getBoundsFuncForConfig, getElementsFuncForConfig } from './nodePositionUtils';
import { getDndAreaElement } from '../dom/dndAreaDomSelectors';
import { getDndAreaComputedMargin } from '../dom/margin';
const columnExtractorBuilder = (nodePositions, previousNodePositionsById, {
  getBoundsForConfig,
  getElementsForConfig,
  layoutSectionId
}) => ({
  column,
  columnId,
  depth,
  unitsWide,
  parentRowId,
  parentRowHasRoomForMoreColumns,
  numChildRows,
  firstChildRowId,
  lastChildRowId,
  previousColumnId,
  nextColumnId,
  rowCallback
}) => {
  const prevPosition = previousNodePositionsById[columnId];
  const columnElement = getElementsForConfig(column)[0];
  const bounds = getBoundsForConfig(columnElement);
  const newPosition = Object.assign({}, bounds, {
    id: columnId,
    depth,
    unitsWide,
    parentId: parentRowId,
    layoutSectionId,
    parentRowHasRoomForMoreColumns,
    numChildRows,
    firstChildRowId,
    lastChildRowId,
    previousColumnId,
    nextColumnId,
    isColumn: true,
    isModule: column.isModule(),
    hasLayoutStyles: column.hasLayoutStyles(),
    isFixedPosition: bounds.isFixedPosition,
    isAbsolutePosition: bounds.isAbsolutePosition
  });
  if (prevPosition && prevPosition.id === columnId && shallowEqual(prevPosition, newPosition)) {
    nodePositions.push(prevPosition);
  } else {
    nodePositions.push(newPosition);
  }
  return rowCallback();
};
const rowExtractorBuilder = (nodePositions, previousNodePositionsById, {
  getBoundsForConfig,
  getElementsForConfig,
  layoutSectionId
}) => ({
  // i,
  rowId,
  previousRowId,
  nextRowId,
  parentColumnId,
  numChildColumns,
  depth,
  row,
  columnCallback
}) => {
  const prevPosition = previousNodePositionsById[rowId];
  const rowElement = getElementsForConfig(row)[0];
  const bounds = getBoundsForConfig(rowElement);
  const newPosition = Object.assign({}, bounds, {
    id: rowId,
    depth,
    parentId: parentColumnId,
    layoutSectionId,
    previousRowId,
    nextRowId,
    numChildColumns,
    isTopMostRow: depth === 1,
    isRow: true,
    hasLayoutStyles: row.hasLayoutStyles(),
    isEmbed: row.isEmbed()
  });
  if (prevPosition && prevPosition.id === rowId && shallowEqual(prevPosition, newPosition)) {
    nodePositions.push(prevPosition);
  } else {
    nodePositions.push(newPosition);
  }
  return columnCallback();
};
const getLayoutTreePosition = (id, bounds, margin) => {
  return Object.assign({}, bounds, {
    id,
    depth: 0,
    margin: {
      top: margin.top,
      left: margin.left
    },
    isColumn: true
  });
};

// The result is a map of arrays where the key is the rootId of each layout section,
// and the arrays contain all the node positions for that layout section
// If no "layoutIteratorMap" is given, only calculate the outer bounds for each layout section
//
// Also, to help selector caching, this function will try to re-use previous node position references
// if possible (for indivdual nodes _AND_ the whole positions array if everything is an exact match)
const calculateLayoutTreeNodePositions = (layoutTreeIds, layoutIteratorMap, previousNodePositions = {}, subConfig = {}) => {
  const newNodePositions = {};
  if (subConfig.startCallback) {
    subConfig.startCallback(subConfig);
  }
  const getBoundsForConfig = getBoundsFuncForConfig(subConfig);
  const getElementsForConfig = getElementsFuncForConfig(subConfig);
  const prevNodePositionsById = arrangeNodePositionSubsetById(previousNodePositions, subConfig);
  layoutTreeIds.forEach(id => {
    const treeRootEl = getDndAreaElement(id);
    const newBounds = getBoundsForConfig(treeRootEl);
    const newMargin = getDndAreaComputedMargin(treeRootEl);
    const layoutTreePosition = getLayoutTreePosition(id, newBounds, newMargin);
    const previousNodePositionsForArea = previousNodePositions[id] || [];
    const previousRootPosition = previousNodePositionsForArea[0];
    const previousNodePositionsForAreaById = prevNodePositionsById[id] || {};
    const newNodePositionsForArea = [];

    // Manually do a shallow equal check on the nested margin property (that is the only nested
    // thing on a root position that wouldn't be correctly checked by a single `shallowEqual` call)
    if (previousRootPosition && shallowEqual(previousRootPosition.margin, layoutTreePosition.margin)) {
      layoutTreePosition.margin = previousRootPosition.margin;
    }
    if (previousRootPosition && previousRootPosition.id === id && shallowEqual(previousRootPosition, layoutTreePosition)) {
      newNodePositionsForArea.push(previousRootPosition);
    } else {
      newNodePositionsForArea.push(layoutTreePosition);
    }
    if (layoutIteratorMap) {
      layoutIteratorMap[id]({
        rowCallback: rowExtractorBuilder(newNodePositionsForArea, previousNodePositionsForAreaById, {
          getBoundsForConfig,
          getElementsForConfig,
          layoutSectionId: id
        }),
        columnCallback: columnExtractorBuilder(newNodePositionsForArea, previousNodePositionsForAreaById, {
          getBoundsForConfig,
          getElementsForConfig,
          layoutSectionId: id
        })
      });
    }

    // Compare if the node position stack for this specific layout section is the same has before.
    // And if it is return the previous position array reference (this works because `rowExtractorBuilder`
    // and `columnExtractorBuilder` are already returning the same position reference for every item in
    // the array if it hasn't changed)
    if (shallowEqual(newNodePositionsForArea, previousNodePositionsForArea)) {
      newNodePositions[id] = previousNodePositionsForArea;
    } else {
      newNodePositions[id] = newNodePositionsForArea;
    }
  });
  if (subConfig.endCallback) {
    subConfig.endCallback(subConfig);
  }

  // If _all_ the node positions for every layout section are the same, return the previous reference
  if (shallowEqual(newNodePositions, previousNodePositions)) {
    return previousNodePositions;
  }
  return newNodePositions;
};
export default calculateLayoutTreeNodePositions;